import { motion } from "framer-motion";
import { ArrowUpRight, Dumbbell, Droplets, Sparkles, HeartPulse, Gift, Calendar } from "lucide-react";
import { BOOK_URL, IMAGES } from "@/lib/site";
import { FadeUp } from "@/components/motion-ui";

const services = [
  {
    id: "bbl",
    title: "BBL Natural Sculpt",
    subtitle: "Sculpting Signature",
    description:
      "BBL Natural Sculpt, le rituel 4-en-1 signature de Perle Lama : sport intense + drainage aux huiles essentielles + pierres semi-précieuses + sculptural. La méthode 100% naturelle pour redessiner ta silhouette et galber tes fessiers — zéro chirurgie, résultats bien visibles.",
    price: "60€",
    priceSuffix: "la séance",
    priceNote: "49€/séance dans le forfait 2 rdv/semaine",
    availableFrom: "Disponible le 5 décembre 2026",
    duration: "60 min",
    icon: Sparkles,
    image: IMAGES.heroFitness,
    span: "md:col-span-7",
    bg: "bg-pink-50",
    accent: "text-pink-600",
    pill: "bg-pink-600 text-white",
  },
  {
    id: "drainage",
    title: "Drainage Lymphatique",
    subtitle: "Détox & Légèreté",
    description:
      "Massage drainant pur, sans sport : huiles ayurvédiques + rituel aux pierres semi-précieuses (jade, quartz rose, améthyste). Relance la circulation, dégonfle les jambes, élimine les toxines et illumine la peau.",
    price: "60€",
    priceSuffix: "la séance",
    priceNote: "49€/séance dans le forfait 2 rdv/semaine",
    availableFrom: "Disponible le 31 octobre 2026",
    duration: "50 min",
    icon: Droplets,
    image: IMAGES.lymphatic,
    span: "md:col-span-5",
    bg: "bg-cyan-50",
    accent: "text-cyan-600",
    pill: "bg-cyan-500 text-white",
  },
  {
    id: "burnout",
    title: "Anti Burn-Out",
    subtitle: "Sport • Massage • Sculptural",
    description:
      "Le combo signature : sport + massage + sculptural dans une même séance, 100% individuel et sur-mesure selon tes objectifs. Invite une amie / un ami à partager ta séance et profitez-en ensemble 💞 Reconnexion corps & esprit, énergie tropicale 🌴, retour à la vitalité.",
    price: "Sur mesure",
    priceSuffix: "",
    priceNote: "Séances individuelles, selon tes objectifs",
    availableFrom: "Disponible le 31 octobre 2026",
    duration: "Programme personnalisé",
    icon: HeartPulse,
    image: IMAGES.facialMassage,
    span: "md:col-span-6",
    bg: "bg-yellow-50",
    accent: "text-amber-600",
    pill: "bg-amber-400 text-neutral-900",
  },
  {
    id: "elastiques",
    title: "Élastiques LAGIGAFAM",
    subtitle: "Entraînement à la maison",
    description:
      "Les élastiques signature de Perle Lama : ultra-résistants, multi-niveaux (rose, jaune, blanc/rouge) + 8 fiches d'exercices illustrées. Bouge, progresse, répète — chez toi, à ton rythme.",
    price: "Pack dès 39€",
    priceSuffix: "",
    priceNote: "Livraison rapide incluse",
    availableFrom: null,
    duration: "Livraison rapide",
    icon: Dumbbell,
    image: IMAGES.equipment,
    span: "md:col-span-6",
    bg: "bg-fuchsia-50",
    accent: "text-fuchsia-600",
    pill: "bg-fuchsia-600 text-white",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative px-5 md:px-8 py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        <FadeUp className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-pink-600 mb-3">
              ✦ Nos prestations
            </span>
            <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tight text-neutral-900 leading-[0.95]">
              Sculpte, drain,
              <br />
              <span className="text-pink-600">brille.</span>
            </h2>
          </div>
          <p className="max-w-md text-neutral-600 text-base md:text-lg font-medium">
            Quatre rituels signature pensés par Perle Lama pour transformer ton
            corps et ton énergie — en distanciel, à domicile ou en salle.
          </p>
        </FadeUp>

        {/* Offre découverte */}
        <FadeUp delay={0.1}>
        <a
          href={BOOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="discovery-banner"
          className="group relative mb-10 flex flex-col md:flex-row items-center justify-between gap-5 bg-gradient-to-r from-pink-600 via-fuchsia-500 to-pink-600 bg-[length:200%_100%] hover:bg-right rounded-[2rem] p-6 md:p-8 shadow-[0_20px_50px_-10px_rgba(255,0,127,0.5)] overflow-hidden transition-[background-position] duration-700"
        >
          <div className="flex items-center gap-5">
            <div className="h-14 w-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
              <Gift className="text-white" size={26} />
            </div>
            <div className="text-white">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-90">
                Offre découverte
              </p>
              <p className="font-display text-2xl md:text-3xl font-black uppercase leading-tight mt-1">
                Ta 1<sup>re</sup> séance à{" "}
                <span className="bg-yellow-300 text-neutral-900 px-2 rounded-md">
                  20€
                </span>{" "}
                seulement
              </p>
              <p className="text-sm font-semibold text-white/90 mt-1">
                Teste la méthode Perle Lama sans engagement — valable pour
                sport ou drainage.
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 bg-white text-pink-700 px-6 h-12 rounded-full font-bold shadow-md shrink-0 group-hover:scale-105 transition-transform">
            Je découvre <ArrowUpRight size={16} />
          </span>
        </a>
        </FadeUp>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {services.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.9, delay: idx * 0.08, ease: [0.22, 0.8, 0.3, 1] }}
                whileHover={{ y: -6 }}
                className={`${s.span} ${s.bg} group relative overflow-hidden rounded-[2rem] border border-pink-100 p-6 md:p-8 flex flex-col min-h-[460px] hover:shadow-[0_20px_60px_-10px_rgba(255,0,127,0.35)] transition-shadow duration-500`}
              >
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div className={`h-12 w-12 rounded-2xl bg-white shadow-md flex items-center justify-center ${s.accent}`}>
                    <Icon size={22} />
                  </div>
                  <span className={`${s.pill} text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full`}>
                    {s.subtitle}
                  </span>
                </div>

                <h3 className="font-display text-3xl md:text-4xl font-black text-neutral-900 leading-tight">
                  {s.title}
                </h3>
                {s.availableFrom && (
                  <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-neutral-900 bg-white/80 backdrop-blur px-3 py-1.5 rounded-full self-start border border-neutral-200 shadow-sm">
                    <Calendar size={13} className={s.accent} />
                    {s.availableFrom}
                  </p>
                )}
                <p className="mt-4 text-neutral-700 leading-relaxed font-medium text-[15px] flex-grow">
                  {s.description}
                </p>

                <div className="mt-6 flex flex-col items-start gap-3 relative z-10 max-w-[60%]">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-neutral-500">
                      {s.duration}
                    </p>
                    <p className={`font-display text-3xl font-black ${s.accent} flex items-baseline gap-1`}>
                      {s.price}
                      {s.priceSuffix && (
                        <span className="text-sm font-bold text-neutral-500">
                          {s.priceSuffix}
                        </span>
                      )}
                    </p>
                    {s.priceNote && (
                      <p className="text-xs font-semibold text-neutral-600 mt-1">
                        ✨ {s.priceNote}
                      </p>
                    )}
                  </div>
                  <a
                    href={BOOK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-book-${s.id}`}
                    className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-pink-600 text-white px-5 h-12 rounded-full font-bold text-sm transition-colors shadow-lg"
                  >
                    Réserver <ArrowUpRight size={16} />
                  </a>
                </div>

                <div className="absolute -bottom-10 -right-10 w-44 h-44 rounded-3xl overflow-hidden opacity-90 group-hover:scale-105 transition-transform duration-700 pointer-events-none">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
